import {ServerError, withStartup} from '@yandex-delivery/core/utils/network'
import type {FetchFunction, TypedRequestConfig} from '@yandex-delivery/core/utils/network/types'
import {config} from 'src/shared/config'

const USER_ID_HEADER = 'x-yataxi-userid'

const isUserIdError = (error: unknown): boolean => {
  if (error instanceof ServerError) {
    // 400 — невалидный userId, 403 — userId не совпадает с ожидаемым
    return error.status === 400 || error.status === 403
  }
  return false
}

/**
 * Middleware для работы с x-yataxi-userid
 *
 * Если userId передан в config (от сервера) — использует его напрямую,
 * избегая запроса к /4.0/startup.
 * При ошибке 400/403 (невалидный или отвергнутый userId) — повторяет
 * запрос через /4.0/startup один раз.
 *
 * Если userId нет — сразу использует стандартный withStartup middleware,
 * который запрашивает userId через /4.0/startup
 */
export const withStartupUserId = <TReq = unknown, TRes = unknown>(
  fetchFn: FetchFunction<TReq, TRes>,
): FetchFunction<TReq, TRes> => {
  // Если userId нет в конфиге — сразу используем withStartup (без изменений)
  if (!config.userId) {
    return withStartup(fetchFn)
  }

  // Создаём fallback-версию через withStartup (ленивая инициализация)
  let startupFetchFn: FetchFunction<TReq, TRes> | null = null
  const getStartupFetch = () => {
    if (!startupFetchFn) {
      startupFetchFn = withStartup(fetchFn)
    }
    return startupFetchFn
  }

  return function withUserIdFromConfig(requestConfig: TypedRequestConfig<TReq, TRes>) {
    // Оптимистично используем userId из конфига
    return fetchFn({
      ...requestConfig,
      headers: {
        ...requestConfig.headers,
        [USER_ID_HEADER]: config.userId,
      },
    }).catch((error: unknown) => {
      // Если ошибка связана с userId — повторяем запрос через startup один раз
      if (isUserIdError(error)) {
        console.warn('[withStartupUserId] userId from config rejected, falling back to /4.0/startup', {
          status: (error as ServerError).status,
        })
        return getStartupFetch()(requestConfig)
      }
      throw error
    })
  }
}
